import type {
  CommonNetworkProfile,
  LicenceProfileTypesEdit,
  ListResponse,
  ProfileNote,
  ProfileNotes,
  ProfileResponse,
  ProfileTypesEdit,
} from '@shared/schemas';
import {
  all,
  call,
  getContext,
  put,
  race,
  select,
  take,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';

import type { Notifier } from '@/store/notifier';
import type { RootState } from '@/store/root-reducer';
import { logger } from '@/utils/logger';

import type { NoteOperation } from '../domain/profile-detail.model';
import type { TypeCategories } from '../domain/profile-detail.rules';
import { isNoteGone } from '../domain/profile-notes.rules';
import type { ProfileDetailGateway } from '../infrastructure/profile-detail.gateway';
import {
  addToListRequested,
  assignableListsRequested,
  commonNetworkRequested,
  contactStatusRequested,
  deactivateRequested,
  enrichRequested,
  expertValidationRequested,
  licenceTypeAddRequested,
  licenceTypeRemoveRequested,
  licenceTypesEditRequested,
  loadRequested,
  noteAddRequested,
  noteDeleteRequested,
  notesLoadRequested,
  noteUpdateRequested,
  reactivateRequested,
  typeAddRequested,
  typeCategoriesRequested,
  typeRemoveRequested,
  typeRestoreRequested,
  typesEditRequested,
} from './profile-detail.actions';
import { profileDetailActions } from './profile-detail.slice';

/** Best-effort human message for a failure snackbar. */
function toMessage(e: unknown, fallback = 'Enrichment failed'): string {
  if (e instanceof TypeError) {
    return 'Network error; please check your connection and retry.';
  }
  return e instanceof Error && e.message ? e.message : fallback;
}

/** WORKER: load the profile by id. Keeps the current profile on a re-load. */
function* loadWorker(action: ReturnType<typeof loadRequested>) {
  const gateway: ProfileDetailGateway = yield getContext('profileDetailGateway');
  yield put(profileDetailActions.loadStarted());
  try {
    const profile = (yield call(gateway.loadProfile, action.payload)) as ProfileResponse;
    yield put(profileDetailActions.loadSucceeded(profile));
  } catch {
    yield put(profileDetailActions.loadFailed());
  }
}

/**
 * WORKER: trigger enrichment. On success the live `EnrichmentPanel` drawer opens
 * (driven by `enrichAccepted`) and owns progress + completion; a POST failure maps
 * to a snackbar message. Unlike company enrichment there is no saga-side poll; the
 * panel streams progress and the page issues a silent `loadRequested` on complete.
 */
function* enrichWorker(action: ReturnType<typeof enrichRequested>) {
  const { id, force } = action.payload;
  const gateway: ProfileDetailGateway = yield getContext('profileDetailGateway');
  yield put(profileDetailActions.enrichStarted());
  try {
    yield call(gateway.enrich, id, force);
    yield put(profileDetailActions.enrichAccepted());
  } catch (e) {
    yield put(profileDetailActions.enrichFailed(toMessage(e)));
  }
}

/**
 * WORKER: deactivate the profile globally, then reload it.
 *
 * The reload is what makes the change visible: the page reads `deactivated_at`
 * to swap the CTA for the deactivation banner, and re-reading the row is also
 * how it picks up the attribution the backend just wrote (who, when). The slice
 * therefore has no `succeeded` transition; there is nothing to hold.
 */
function* deactivateWorker(action: ReturnType<typeof deactivateRequested>) {
  const gateway: ProfileDetailGateway = yield getContext('profileDetailGateway');
  yield put(profileDetailActions.deactivateStarted());
  try {
    yield call(gateway.deactivate, action.payload);
    yield put(profileDetailActions.deactivateCleared());
    yield put(loadRequested(action.payload));
  } catch (e) {
    yield put(profileDetailActions.deactivateFailed(toMessage(e, 'Deactivation failed')));
  }
}

/**
 * WORKER: reactivate the profile, then reload it (#659).
 *
 * Same reload as `deactivateWorker`: `deactivated_at` going back to null is what
 * takes the banner away and brings the record actions back. The `succeeded`
 * step only carries what the reload cannot: whether scans will now spare it.
 */
function* reactivateWorker(action: ReturnType<typeof reactivateRequested>) {
  const gateway: ProfileDetailGateway = yield getContext('profileDetailGateway');
  yield put(profileDetailActions.reactivateStarted());
  try {
    const { immunised } = (yield call(gateway.reactivate, action.payload)) as {
      immunised: boolean;
    };
    yield put(profileDetailActions.reactivateSucceeded({ immunised }));
    yield put(loadRequested(action.payload));
  } catch (e) {
    yield put(profileDetailActions.reactivateFailed(toMessage(e, 'Reactivation failed')));
  }
}

/** The id of the profile on screen, or null while none is (a reset, a load). */
const selectShownProfileId = (state: RootState) => state.profileDetail.profile?.id ?? null;

/** Said when the change landed but the page could not show it (#754). */
export const EXPERT_VALIDATION_RELOAD_FAILED =
  'The expert validation was saved, but the profile could not be reloaded. Refresh the page to see it.';

/**
 * WORKER: set or remove "Verified by Invyo Expert" (#754), then reload.
 *
 * The reload runs inside the worker rather than through `put`: the badge stays
 * disabled until the reloaded profile shows the new state, so a second click
 * cannot act on the old one. A failure leaves the profile as it was (nothing
 * is optimistic) and keeps the message.
 *
 * The page may have moved to another profile while the POST ran: the reload is
 * then skipped, and a reload that lands after such a move is dropped, so the
 * profile on screen is never replaced by the one validated.
 */
function* expertValidationWorker(action: ReturnType<typeof expertValidationRequested>) {
  const { id, validated } = action.payload;
  const gateway: ProfileDetailGateway = yield getContext('profileDetailGateway');
  yield put(profileDetailActions.expertValidationStarted(id));
  try {
    yield call(gateway.setExpertValidation, id, validated);
  } catch (e) {
    yield put(
      profileDetailActions.expertValidationFailed({
        profileId: id,
        message: toMessage(e, 'Could not update the expert validation'),
      }),
    );
    return;
  }
  const shownAfterPost: string | null = yield select(selectShownProfileId);
  if (shownAfterPost === id) {
    try {
      const profile = (yield call(gateway.loadProfile, id)) as ProfileResponse;
      const shownAfterReload: string | null = yield select(selectShownProfileId);
      if (shownAfterReload === id) {
        yield put(profileDetailActions.loadSucceeded(profile));
      }
    } catch {
      yield put(
        profileDetailActions.expertValidationFailed({
          profileId: id,
          message: EXPERT_VALIDATION_RELOAD_FAILED,
        }),
      );
      return;
    }
  }
  yield put(profileDetailActions.expertValidationCleared(id));
}

/**
 * WATCHER of the badge (#754): one request per profile at a time, a double
 * click sends one. The lock is per profile, so a click on another profile is
 * never swallowed, and a reset of the page (leaving it, opening another
 * profile) cancels the run and frees its lock.
 */
function* expertValidationWatcher() {
  const inFlight = new Set<string>();
  yield takeEvery(
    expertValidationRequested.type,
    function* guarded(action: ReturnType<typeof expertValidationRequested>) {
      const { id } = action.payload;
      if (inFlight.has(id)) return;
      inFlight.add(id);
      try {
        yield race({
          done: call(expertValidationWorker, action),
          reset: take(profileDetailActions.resetForm.type),
        });
      } finally {
        inFlight.delete(id);
      }
    },
  );
}

/**
 * WORKER: load the Network tab rows.
 *
 * A failure lands an empty, settled tab and stays silent (no snackbar): this is
 * deliberate parity with the `useApi` version the tab used, whose `error` the UI
 * never read, so a failed load renders the same empty state as a genuinely empty
 * network. `takeLatest` replaces that hook's AbortController.
 */
function* commonNetworkWorker(action: ReturnType<typeof commonNetworkRequested>) {
  const gateway: ProfileDetailGateway = yield getContext('profileDetailGateway');
  yield put(profileDetailActions.commonNetworkLoadStarted());
  try {
    const rows = (yield call(
      gateway.loadCommonNetwork,
      action.payload,
    )) as CommonNetworkProfile[];
    yield put(profileDetailActions.commonNetworkLoaded(rows));
  } catch {
    yield put(profileDetailActions.commonNetworkLoaded([]));
  }
}

/**
 * WORKER: an admin's contact verification, then reload the profile.
 *
 * Reload rather than patching the row in place, exactly like `deactivateWorker`:
 * the response carries all four status columns, but the page reads the profile
 * for a dozen other things and one source of truth is cheaper than two that can
 * drift. The slice therefore has no `succeeded` transition.
 */
function* contactStatusWorker(action: ReturnType<typeof contactStatusRequested>) {
  const gateway: ProfileDetailGateway = yield getContext('profileDetailGateway');
  const { id, contact, verified } = action.payload;
  yield put(profileDetailActions.contactStatusStarted(contact));
  try {
    yield call(gateway.setContactStatus, id, contact, verified);
    yield put(profileDetailActions.contactStatusCleared());
    yield put(loadRequested(id));
  } catch (e) {
    yield put(
      profileDetailActions.contactStatusFailed(toMessage(e, 'Could not update the contact status')),
    );
  }
}

/** WORKER: the lists the admin's picker offers. A failure leaves the picker empty with a note. */
function* assignableListsWorker() {
  const gateway: ProfileDetailGateway = yield getContext('profileDetailGateway');
  yield put(profileDetailActions.assignableListsLoadStarted());
  try {
    const lists = (yield call(gateway.loadAssignableLists)) as ListResponse[];
    yield put(profileDetailActions.assignableListsLoaded(lists));
  } catch {
    yield put(profileDetailActions.assignableListsFailed());
  }
}

/**
 * WORKER: each type's main category, for the admin's type chip (#629). A failure
 * leaves it null: the chip then names the type alone, as it did before.
 */
function* typeCategoriesWorker() {
  const gateway: ProfileDetailGateway = yield getContext('profileDetailGateway');
  try {
    const categories = (yield call(gateway.loadTypeCategories)) as TypeCategories;
    yield put(profileDetailActions.typeCategoriesLoaded(categories));
  } catch {
    // Nothing to tell the admin: the chip without its category is still right.
  }
}

/**
 * WORKER: add the profile to one list, then reload it: the reload is what puts
 * the list in the page's Lists block, the same reasoning as `deactivateWorker`.
 */
function* addToListWorker(action: ReturnType<typeof addToListRequested>) {
  const gateway: ProfileDetailGateway = yield getContext('profileDetailGateway');
  const { profileId, listId, listName } = action.payload;
  yield put(profileDetailActions.addToListStarted());
  try {
    yield call(gateway.addToList, listId, profileId);
    yield put(profileDetailActions.addToListSucceeded(listName));
    yield put(loadRequested(profileId));
  } catch (e) {
    yield put(profileDetailActions.addToListFailed(toMessage(e, 'Could not add the profile to the list')));
  }
}

/** WORKER: open the admin's "Types" dialog (#652). A failure is said in the dialog. */
function* typesEditWorker(action: ReturnType<typeof typesEditRequested>) {
  const gateway: ProfileDetailGateway = yield getContext('profileDetailGateway');
  yield put(profileDetailActions.typesEditLoadStarted());
  try {
    const edit = (yield call(gateway.loadTypes, action.payload.id)) as ProfileTypesEdit;
    yield put(profileDetailActions.typesEditLoaded(edit));
  } catch {
    yield put(profileDetailActions.typesEditLoadFailed());
  }
}

/**
 * WORKER: take a type off, put it back, or add one by hand (#701), then reload the
 * profile so the chips follow (the page only: the drawer redraws its own). The answer already carries the dialog's new state, the primary type
 * included, as the server ordered it.
 *
 * `takeEvery`: a write cancelled by the next click would land on the server and
 * never reach the page.
 */
function* typeEditWorker(
  action:
    | ReturnType<typeof typeRemoveRequested>
    | ReturnType<typeof typeRestoreRequested>
    | ReturnType<typeof typeAddRequested>,
) {
  const gateway: ProfileDetailGateway = yield getContext('profileDetailGateway');
  const { id, type, reloadProfile = true } = action.payload;
  const [write, failure] = typeRemoveRequested.match(action)
    ? [gateway.removeType, `Could not remove ${type}`]
    : typeAddRequested.match(action)
      ? [gateway.addType, `Could not add ${type}`]
      : [gateway.restoreType, `Could not restore ${type}`];
  yield put(profileDetailActions.typesEditStarted(type));
  try {
    const edit = (yield call(write, id, type)) as ProfileTypesEdit;
    yield put(profileDetailActions.typesEditLoaded(edit));
    if (reloadProfile) yield put(loadRequested(id));
  } catch (e) {
    yield put(profileDetailActions.typesEditFailed(toMessage(e, failure)));
  }
}

/** WORKER: open a client's "Types" dialog, for their licence (#723). A failure is said in the dialog. */
function* licenceTypesEditWorker(action: ReturnType<typeof licenceTypesEditRequested>) {
  const gateway: ProfileDetailGateway = yield getContext('profileDetailGateway');
  yield put(profileDetailActions.licenceTypesEditLoadStarted());
  try {
    const edit = (yield call(gateway.loadLicenceTypes, action.payload.id)) as LicenceProfileTypesEdit;
    yield put(profileDetailActions.licenceTypesEditLoaded(edit));
  } catch {
    yield put(profileDetailActions.licenceTypesEditLoadFailed());
  }
}

/**
 * WORKER: add a type for the caller's licence, or remove one it added (#723), then
 * reload the profile so the chips follow (the page only: the drawer redraws its
 * own). `takeEvery`, for the reason `typeEditWorker` gives.
 */
function* licenceTypeEditWorker(
  action: ReturnType<typeof licenceTypeAddRequested> | ReturnType<typeof licenceTypeRemoveRequested>,
) {
  const gateway: ProfileDetailGateway = yield getContext('profileDetailGateway');
  const { id, type, reloadProfile = true } = action.payload;
  const [write, failure] = licenceTypeAddRequested.match(action)
    ? [gateway.addLicenceType, `Could not add ${type}`]
    : [gateway.removeLicenceType, `Could not remove ${type}`];
  yield put(profileDetailActions.licenceTypesEditStarted(type));
  try {
    const edit = (yield call(write, id, type)) as LicenceProfileTypesEdit;
    yield put(profileDetailActions.licenceTypesEditLoaded(edit));
    if (reloadProfile) yield put(loadRequested(id));
  } catch (e) {
    yield put(profileDetailActions.licenceTypesEditFailed(toMessage(e, failure)));
  }
}

/** WORKER: load the caller's licence's notes on the profile, for the Notes tab (#676). */
function* notesLoadWorker(action: ReturnType<typeof notesLoadRequested>) {
  const gateway: ProfileDetailGateway = yield getContext('profileDetailGateway');
  const profileId = action.payload;
  yield put(profileDetailActions.notesLoadStarted(profileId));
  try {
    const notes = (yield call(gateway.loadNotes, profileId)) as ProfileNotes;
    yield put(profileDetailActions.notesLoadSucceeded({ profileId, notes }));
  } catch (e) {
    yield put(profileDetailActions.notesLoadFailed({ profileId, message: toMessage(e, 'Failed to load the notes.') }));
  }
}

type NoteChange =
  | ReturnType<typeof noteAddRequested>
  | ReturnType<typeof noteUpdateRequested>
  | ReturnType<typeof noteDeleteRequested>;

/**
 * WORKER: add, edit or delete one note (#743); the server's copy of the note
 * joins or replaces the shown one, a deleted note leaves the list, and so does
 * one the server no longer has (404 Note on an edit or a delete). Every
 * failure is announced through the notifier; a success is the tab's to announce.
 */
function* noteChangeWorker(action: NoteChange) {
  const gateway: ProfileDetailGateway = yield getContext('profileDetailGateway');
  const profileId = action.payload.id;
  const operation: NoteOperation = noteAddRequested.match(action)
    ? 'add'
    : noteUpdateRequested.match(action)
      ? 'update'
      : 'delete';
  const noteId = noteAddRequested.match(action) ? null : action.payload.noteId;
  try {
    yield put(profileDetailActions.notesSaveStarted({ profileId, operation, noteId }));
    if (noteAddRequested.match(action)) {
      const note = (yield call(gateway.addNote, profileId, action.payload.content)) as ProfileNote;
      yield put(profileDetailActions.noteAdded({ profileId, note }));
    } else if (noteUpdateRequested.match(action)) {
      const { noteId: id, content } = action.payload;
      const note = (yield call(gateway.updateNote, profileId, id, content)) as ProfileNote;
      yield put(profileDetailActions.noteUpdated({ profileId, note }));
    } else {
      const { noteId: id } = action.payload;
      yield call(gateway.deleteNote, profileId, id);
      yield put(profileDetailActions.noteDeleted({ profileId, noteId: id }));
    }
  } catch (e) {
    const failure = operation === 'delete' ? 'Failed to delete the note.' : 'Failed to save the note.';
    const message = toMessage(e, failure);
    logger.error('[notes] Note change failed', { operation, profileId, noteId, error: e });
    yield put(profileDetailActions.notesSaveFailed({ profileId, operation, noteId, message, noteGone: isNoteGone(e) }));
    // Announced from here, not by the tab: the reader may have moved to another
    // profile or page, and a failure must still reach them. A broken notifier
    // must not take the watcher (and the root saga) down with it.
    const notifier: Notifier = yield getContext('notifier');
    try {
      yield call([notifier, notifier.error], message);
    } catch (notifyError) {
      logger.warn('[notes] Could not announce the failure', notifyError);
    }
  }
}

/**
 * WATCHER: one note change at a time per profile, profiles side by side
 * (#743). A change asked while the same profile's is in flight (a double click
 * on Save) is dropped, as `takeLeading` would; another profile's goes at once.
 */
function* noteChangeWatcher() {
  const inFlight = new Set<string>();
  yield takeEvery(
    [noteAddRequested.type, noteUpdateRequested.type, noteDeleteRequested.type],
    function* leadingPerProfile(action: NoteChange) {
      const profileId = action.payload.id;
      if (inFlight.has(profileId)) {
        // The tab shows the profile busy while a change is in flight, so this
        // should not happen; dropped (no double POST), but never silently.
        logger.warn('[notes] Change dropped: another one is in flight for profile', profileId, action.type);
        return;
      }
      inFlight.add(profileId);
      try {
        yield call(noteChangeWorker, action);
      } finally {
        inFlight.delete(profileId);
      }
    },
  );
}

/** WATCHERS. */
export function* profileDetailSaga() {
  yield all([
    takeLatest(loadRequested.type, loadWorker),
    takeLatest(enrichRequested.type, enrichWorker),
    takeLatest(deactivateRequested.type, deactivateWorker),
    takeLatest(reactivateRequested.type, reactivateWorker),
    takeLatest(contactStatusRequested.type, contactStatusWorker),
    // One request per profile at a time: a double click on the badge sends one.
    call(expertValidationWatcher),
    takeLatest(commonNetworkRequested.type, commonNetworkWorker),
    takeLatest(assignableListsRequested.type, assignableListsWorker),
    takeLatest(typeCategoriesRequested.type, typeCategoriesWorker),
    takeLatest(addToListRequested.type, addToListWorker),
    takeLatest(typesEditRequested.type, typesEditWorker),
    takeEvery([typeRemoveRequested.type, typeRestoreRequested.type, typeAddRequested.type], typeEditWorker),
    takeLatest(licenceTypesEditRequested.type, licenceTypesEditWorker),
    takeEvery([licenceTypeAddRequested.type, licenceTypeRemoveRequested.type], licenceTypeEditWorker),
    takeLatest(notesLoadRequested.type, notesLoadWorker),
    // A double click on Save must not send the same note twice: one change at
    // a time per profile, as each tab shows its profile's save status.
    noteChangeWatcher(),
  ]);
}
